import { computed } from 'vue';

export interface StarBlock {
  header: string | null;
  body: string;
}

const STAR_HEADER_KEYS = ['situation_header', 'task_header', 'action_header', 'result_header'] as const;

/**
 * Single source of truth for how a STAR header is rendered inside the
 * concatenated description, e.g. "---- Contexte ----". Used both when building
 * the description (BStarFieldset) and when parsing it back (toBlocks), so the
 * two never drift apart.
 */
export const formatStarHeader = (label: string): string => `---- ${label} ----`;

/**
 * Parse a concatenated STAR description into blocks. The expected shape is, for
 * each block: a formatted header line, a blank line, then the value; blocks are
 * separated by blank lines. Lines matching a known formatted header are flagged
 * so consumers can render them in bold. A free-text description (no recognised
 * header) is returned as header-less block(s).
 */
export function useStarDescription() {
  const { t } = useI18n();

  const headerSet = computed(
    () =>
      new Set(
        ['company', 'freelance'].flatMap((ctx) =>
          STAR_HEADER_KEYS.map((k) => formatStarHeader(t(`star_fieldset.${ctx}.${k}`))),
        ),
      ),
  );

  const toBlocks = (description?: string | null): StarBlock[] => {
    const desc = description || '';
    const headers = headerSet.value;
    const chunks = desc
      .split(/\n{2,}/)
      .map((chunk) => chunk.trim())
      .filter((chunk) => chunk.length > 0);

    const blocks: StarBlock[] = [];
    let current: StarBlock | null = null;

    for (const chunk of chunks) {
      if (headers.has(chunk)) {
        current = { header: chunk, body: '' };
        blocks.push(current);
      } else if (current) {
        current.body = current.body ? `${current.body}\n\n${chunk}` : chunk;
      } else {
        blocks.push({ header: null, body: chunk });
      }
    }

    return blocks;
  };

  return { toBlocks };
}
